import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trash, Envelope, ShieldCheck, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';

import { API_URL } from '../config/api';

export const PrivacyPolicyPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/account/delete`, {
        withCredentials: true
      });
      toast.success('Your account has been permanently deleted');
      await logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy"
        description="How TurfPulse collects, uses, and protects your personal information — GDPR, CCPA, and our commitment to privacy-safe analytics."
        path="/privacy"
      />
      {user && <DepartmentNav />}

      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-muted-foreground">Last updated: December 15, 2025</p>
        </div>

        {/* Introduction */}
        <Card>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none pt-6 text-foreground">
            <p className="text-lg">
              TurfPulse ("we," "our," or "us") operates the website getturfpulse.com (the "Service"). 
              This Privacy Policy explains how we collect, use, disclose, and protect your personal information 
              when you use our Service.
            </p>
            <p>
              By using TurfPulse, you agree to the collection and use of information in accordance with this policy.
            </p>
          </CardContent>
        </Card>

        {/* Information We Collect */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">1. Information We Collect</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <h4 className="font-semibold">Personal Information</h4>
            <ul>
              <li><strong>Account Information:</strong> Email address, name, and password when you create an account</li>
              <li><strong>Location Data:</strong> Geographic coordinates (latitude/longitude) for weather-based GDD calculations</li>
              <li><strong>Lawn Profile:</strong> Grass type, property information, and zone configurations</li>
              <li><strong>Usage Data:</strong> Application logs, equipment records, and service history</li>
            </ul>

            <h4 className="font-semibold mt-6">Automatically Collected Information</h4>
            <ul>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>IP address</li>
              <li>Pages visited and time spent on our Service</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h4 className="font-semibold mt-6">Product Analytics (Privacy-Safe)</h4>
            <p className="text-sm text-muted-foreground">
              To understand which features help new users most, we log basic
              page visits once you're signed in: a short page slug (e.g.{' '}
              <code>soil-analysis</code>), a session identifier that resets when
              you close the tab, and how long you stayed. We do <strong>not</strong> record
              your IP for this, do <strong>not</strong> use third-party analytics cookies for
              this, and do <strong>not</strong> capture clicks, typing, or content. Aggregated
              results are visible only to TurfPulse admins and are auto-deleted
              after 90 days. You can opt out anytime from <strong>Profile → Privacy</strong>;
              opting out also purges any existing analytics rows linked to your account.
            </p>
          </CardContent>
        </Card>

        {/* How We Use Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">2. How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our Service</li>
              <li>Calculate Growing Degree Days based on your local weather data</li>
              <li>Send notifications about your lawn care schedule (with your consent)</li>
              <li>Process transactions and manage subscriptions</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Personalize your experience and deliver relevant content</li>
              <li>Analyze usage patterns to improve our Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </CardContent>
        </Card>

        {/* Third-Party Services */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">3. Third-Party Services</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>We may share your information with trusted third-party service providers:</p>
            <ul>
              <li><strong>OpenWeather API:</strong> We share your location coordinates to retrieve local weather data for GDD calculations</li>
              <li><strong>Stripe:</strong> Payment processing for premium subscriptions (we do not store credit card information)</li>
              <li><strong>Resend:</strong> Email delivery for notifications and password resets</li>
              <li><strong>Amazon Associates:</strong> Affiliate links are tracked by Amazon when you click product links</li>
            </ul>
            <p className="mt-4">
              These third parties have their own privacy policies governing their use of your information.
            </p>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">4. Data Retention</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              We retain your personal information for as long as your account is active or as needed to provide 
              you services. You may request deletion of your data at any time (see Your Rights below).
            </p>
            <p>
              After account deletion, we may retain certain information as required by law or for legitimate 
              business purposes (e.g., fraud prevention, legal compliance) for up to 90 days.
            </p>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">5. Data Security</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>HTTP-only cookies for authentication</li>
              <li>Regular security audits</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </CardContent>
        </Card>

        {/* Your Rights - GDPR/CCPA */}
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <ShieldCheck weight="duotone" className="text-primary" size={24} />
              6. Your Rights (GDPR/CCPA)
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul>
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> Request deletion of your data</li>
              <li><strong>Right to Data Portability:</strong> Request your data in a portable format</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
              <li><strong>Right to Object:</strong> Object to processing of your data</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw previously given consent</li>
            </ul>

            <div className="bg-background border rounded-lg p-4 mt-6">
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <Envelope weight="duotone" size={20} className="text-primary" />
                How to Exercise Your Rights
              </h4>
              <p className="mb-2">
                To request a copy of your data, ask questions about our privacy practices, or exercise any of 
                your rights, please contact us at:
              </p>
              <a 
                href="mailto:privacy@getturfpulse.com" 
                className="text-primary hover:underline font-medium"
              >
                privacy@getturfpulse.com
              </a>
              <p className="text-sm text-muted-foreground mt-2">
                We will respond to your request within 30 days.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* California Residents */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">7. California Residents (CCPA)</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with 
              additional rights regarding your personal information:
            </p>
            <ul>
              <li>The right to know what personal information is collected, used, shared, or sold</li>
              <li>The right to delete personal information held by businesses</li>
              <li>The right to opt-out of the sale of personal information</li>
              <li>The right to non-discrimination for exercising your CCPA rights</li>
            </ul>
            <p className="font-medium mt-4">
              TurfPulse does not sell your personal information to third parties.
            </p>
          </CardContent>
        </Card>

        {/* Children's Privacy */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">8. Children's Privacy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              Our Service is not directed to children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and believe 
              your child has provided us with personal information, please contact us at 
              <a href="mailto:privacy@getturfpulse.com" className="text-primary hover:underline ml-1">privacy@getturfpulse.com</a>.
            </p>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">9. Cookies & Tracking</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              We use cookies and similar tracking technologies to enhance your experience:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
              <li><strong>Preference Cookies:</strong> Remember your settings (e.g., dark mode, grass type)</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our Service</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings. Disabling certain cookies may affect Service functionality.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Policy */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">10. Changes to This Policy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. 
              Significant changes will be communicated via email.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">11. Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <ul>
              <li>
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@getturfpulse.com" className="text-primary hover:underline">
                  privacy@getturfpulse.com
                </a>
              </li>
              <li>
                <strong>General Support:</strong>{' '}
                <a href="mailto:support@getturfpulse.com" className="text-primary hover:underline">
                  support@getturfpulse.com
                </a>
              </li>
              <li>
                <strong>Mailing Address:</strong><br />
                TurfPulse<br />
                110 N IH 35 Ste 315 #3365<br />
                Round Rock, Texas 78681
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Delete Account Section - Only for logged in users */}
        {user && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2 text-destructive">
                <Warning weight="duotone" size={24} aria-hidden="true" />
                Delete My Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                You have the right to permanently delete your account and all associated data. 
                This action is irreversible and will remove:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• Your account and profile information</li>
                <li>• All properties and zones</li>
                <li>• Application history and logs</li>
                <li>• Equipment and service records</li>
                <li>• Any contributed spreader settings</li>
              </ul>
              <Button
                data-testid="delete-account-btn"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="mt-4"
                aria-label="Delete my account permanently"
              >
                <Trash weight="bold" size={18} className="mr-2" aria-hidden="true" />
                Delete My Account Permanently
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Back to Home */}
        <div className="text-center pt-4">
          <Button variant="outline" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Warning weight="duotone" size={24} aria-hidden="true" />
              Confirm Account Deletion
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All your data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground" id="delete-instructions">
              Please type <strong className="text-foreground">DELETE</strong> to confirm:
            </p>
            <div>
              <Label htmlFor="delete-confirm">Confirmation</Label>
              <Input
                id="delete-confirm"
                data-testid="delete-confirm-input"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE"
                className="mt-1"
                aria-describedby="delete-instructions"
                aria-required="true"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete-btn"
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmation !== 'DELETE'}
              aria-disabled={isDeleting || deleteConfirmation !== 'DELETE'}
            >
              {isDeleting ? 'Deleting...' : 'Delete Forever'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
